import { useEffect, useState, useCallback } from "react";
import {
  Box, Paper, Stack, Typography, TextField, MenuItem, Chip, Button, Tooltip,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer,
} from "@mui/material";
import GridOnIcon from "@mui/icons-material/GridOn";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import Swal from "sweetalert2";
import { listarInscritos, listarEventos, baixarInscritosExcel, baixarInscritosPdf, marcarPulseira } from "../../services/eventoService";

const fmt = (d) => (d ? new Date(d).toLocaleDateString("pt-BR") : "—");
const moeda = (v) => (v == null ? "—" : Number(v).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }));

const situacao = (i) =>
  i.Status === "X" ? { label: "Cancelada", color: "default" }
  : i.Pago ? { label: "Pago", color: "success" }
  : i.Status === "C" ? { label: "Confirmada", color: "success" }
  : { label: "Aguardando pagamento", color: "warning" };

export default function InscritosList() {
  const [eventos, setEventos] = useState([]);
  const [inscritos, setInscritos] = useState([]);
  const [idEvento, setIdEvento] = useState("");
  const [status, setStatus] = useState("");
  const [pulseira, setPulseira] = useState("");
  const [termo, setTermo] = useState("");
  const [carregando, setCarregando] = useState(false);
  const [salvandoPulseira, setSalvandoPulseira] = useState(null);

  useEffect(() => {
    listarEventos().then((r) => setEventos(r.data)).catch(() => {});
  }, []);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try { setInscritos((await listarInscritos({ idEvento, status, pulseira, termo })).data); }
    catch (e) { Swal.fire("Erro", e?.response?.data || e.message, "error"); }
    finally { setCarregando(false); }
  }, [idEvento, status, pulseira, termo]);

  // Recarrega ao trocar os filtros; o termo é aplicado ao clicar em Buscar/Enter.
  useEffect(() => { carregar(); /* eslint-disable-next-line */ }, [idEvento, status, pulseira]);

  // Marca/desfaz a entrega da pulseira (atualiza só a linha, sem recarregar tudo).
  const alternarPulseira = async (insc) => {
    const entregar = !insc.PulseiraEntregue;
    if (!entregar) {
      const c = await Swal.fire({
        title: "Desfazer entrega?", text: `A pulseira de ${insc.AlunoNome || insc.Candidato} voltará para "não entregue".`,
        icon: "warning", showCancelButton: true, confirmButtonText: "Desfazer", cancelButtonText: "Cancelar",
      });
      if (!c.isConfirmed) return;
    }
    setSalvandoPulseira(insc.Id);
    try {
      const { data } = await marcarPulseira(insc.Id, entregar);
      setInscritos((arr) => arr.map((x) => (x.Id === insc.Id
        ? { ...x, PulseiraEntregue: data.PulseiraEntregue, DtPulseira: data.DtPulseira, UsuarioPulseira: data.UsuarioPulseira }
        : x)));
      Swal.fire({ toast: true, position: "top-end", icon: "success", title: data.mensagem, showConfirmButton: false, timer: 1400 });
    } catch (e) { Swal.fire("Erro", e?.response?.data?.mensagem || e.message, "error"); }
    finally { setSalvandoPulseira(null); }
  };

  const [baixando, setBaixando] = useState("");
  const exportar = async (tipo) => {
    setBaixando(tipo);
    try {
      const fn = tipo === "excel" ? baixarInscritosExcel : baixarInscritosPdf;
      await fn({ idEvento, status, pulseira, termo });
    } catch (e) { Swal.fire("Erro", e?.response?.data?.mensagem || e.message, "error"); }
    finally { setBaixando(""); }
  };

  const totalPago = inscritos.filter((i) => i.Pago).reduce((s, i) => s + Number(i.ValorDevido || 0), 0);
  const nomeEvento = idEvento ? (eventos.find((e) => String(e.Id) === String(idEvento))?.Nome || "") : "Todos os eventos";

  return (
    <Box sx={{ "@media print": { "& .no-print": { display: "none !important" } } }}>
      <Stack className="no-print" direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">Inscritos em eventos</Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="contained" color="success" startIcon={<GridOnIcon />}
            onClick={() => exportar("excel")} disabled={!inscritos.length || !!baixando}>
            {baixando === "excel" ? "Gerando…" : "Excel"}
          </Button>
          <Button variant="contained" color="error" startIcon={<PictureAsPdfIcon />}
            onClick={() => exportar("pdf")} disabled={!inscritos.length || !!baixando}>
            {baixando === "pdf" ? "Gerando…" : "PDF"}
          </Button>
        </Stack>
      </Stack>

      {/* Cabeçalho que só aparece no papel/PDF */}
      <Box sx={{ display: "none", "@media print": { display: "block", mb: 2 } }}>
        <Typography variant="h6">Inscritos — {nomeEvento}</Typography>
        <Typography variant="caption">
          Gerado em {new Date().toLocaleString("pt-BR")} • {inscritos.length} inscrição(ões) • Recebido: {moeda(totalPago)}
        </Typography>
      </Box>

      <Stack className="no-print" direction="row" spacing={1} mb={2} flexWrap="wrap" useFlexGap>
        <TextField select size="small" label="Evento" value={idEvento} onChange={(e) => setIdEvento(e.target.value)} sx={{ minWidth: 260 }}>
          <MenuItem value="">Todos os eventos</MenuItem>
          {eventos.map((e) => <MenuItem key={e.Id} value={e.Id}>{e.Nome}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Situação" value={status} onChange={(e) => setStatus(e.target.value)} sx={{ width: 200 }}>
          <MenuItem value="">Todas</MenuItem>
          <MenuItem value="P">Pagos</MenuItem>
          <MenuItem value="A">Aguardando pagamento</MenuItem>
          <MenuItem value="C">Confirmadas (gratuitas)</MenuItem>
          <MenuItem value="X">Canceladas</MenuItem>
        </TextField>
        <TextField select size="small" label="Pulseira" value={pulseira} onChange={(e) => setPulseira(e.target.value)} sx={{ width: 180 }}>
          <MenuItem value="">Todas</MenuItem>
          <MenuItem value="N">Não entregue</MenuItem>
          <MenuItem value="S">Entregue</MenuItem>
        </TextField>
        <TextField size="small" label="Buscar (nome, CPF, aluno, matrícula, turma)" value={termo}
          onChange={(e) => setTermo(e.target.value)} onKeyDown={(e) => e.key === "Enter" && carregar()} sx={{ minWidth: 320 }} />
        <Button variant="outlined" onClick={carregar}>Buscar</Button>
      </Stack>

      <Stack className="no-print" direction="row" spacing={2} mb={1}>
        <Chip label={`${inscritos.length} inscrição(ões)`} />
        <Chip color="success" variant="outlined" label={`Recebido: ${moeda(totalPago)}`} />
        <Chip color="primary" variant="outlined"
          label={`Pulseiras: ${inscritos.filter((i) => i.PulseiraEntregue).length} de ${inscritos.length}`} />
      </Stack>

      <Paper sx={{ "@media print": { boxShadow: "none" } }}>
        <TableContainer sx={{ maxHeight: "65vh", "@media print": { maxHeight: "none", overflow: "visible" } }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Evento</TableCell>
                <TableCell>Responsável / CPF</TableCell>
                <TableCell>Aluno</TableCell>
                <TableCell>Matrícula</TableCell>
                <TableCell>Turma</TableCell>
                <TableCell>Categoria</TableCell>
                <TableCell align="right">Valor</TableCell>
                <TableCell>Situação</TableCell>
                <TableCell align="center">Pulseira</TableCell>
                <TableCell>Inscrito em</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {inscritos.map((i) => {
                const st = situacao(i);
                return (
                  <TableRow key={i.Id} hover>
                    <TableCell>{i.Evento}</TableCell>
                    <TableCell>{i.Candidato}<br /><Typography variant="caption" color="text.secondary">{i.Cpf}</Typography></TableCell>
                    <TableCell>{i.AlunoNome || "—"}</TableCell>
                    <TableCell>{i.Matricula || "—"}</TableCell>
                    <TableCell>{i.Turma || "—"}</TableCell>
                    <TableCell>{i.Categoria || "—"}</TableCell>
                    <TableCell align="right">{moeda(i.ValorDevido)}</TableCell>
                    <TableCell>
                      <Chip size="small" label={st.label} color={st.color} variant={i.Pago ? "filled" : "outlined"} />
                      {i.PixErro && (
                        <Tooltip title={i.PixErro}><Chip size="small" color="error" variant="outlined" label="Erro PIX" sx={{ ml: 0.5 }} /></Tooltip>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {i.Status === "X" ? (
                        <Typography variant="caption" color="text.disabled">—</Typography>
                      ) : i.PulseiraEntregue ? (
                        <Tooltip title={`Entregue${i.DtPulseira ? ` em ${new Date(i.DtPulseira).toLocaleString("pt-BR")}` : ""}${i.UsuarioPulseira ? ` por ${i.UsuarioPulseira}` : ""} — clique para desfazer`}>
                          <span>
                            <Button size="small" color="success" variant="contained" disableElevation
                              startIcon={<CheckCircleIcon />} disabled={salvandoPulseira === i.Id}
                              onClick={() => alternarPulseira(i)} sx={{ textTransform: "none", minWidth: 118 }}>
                              Entregue
                            </Button>
                          </span>
                        </Tooltip>
                      ) : (
                        <Button size="small" variant="outlined" disabled={salvandoPulseira === i.Id}
                          onClick={() => alternarPulseira(i)} sx={{ textTransform: "none", minWidth: 118 }}>
                          {salvandoPulseira === i.Id ? "…" : "Entregar"}
                        </Button>
                      )}
                    </TableCell>
                    <TableCell>{fmt(i.DtCadastro)}</TableCell>
                  </TableRow>
                );
              })}
              {!inscritos.length && (
                <TableRow><TableCell colSpan={10} align="center">
                  {carregando ? "Carregando…" : "Nenhum inscrito encontrado."}
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
